import { faEdit, faSave, faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useCallback, useState, useMemo, useEffect } from "react"
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import { withAuth } from '../../api/withAuth';
import { formatDate } from "../CustomDateTools";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  createColumnHelper,
  SortingFn,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'

const GoldFinderApi = withAuth(axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_API_URL
}));

const columnHelper = createColumnHelper();



export function DrillHoleModifierTable({ drillHoleModifier, refreshData }) {
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [drillHoleTypes, setDrillHoleTypes] = useState([]);
  const [filters, setFilters] = useState({
    anumber: '',
    multiplier: '',
    description: '',
    holetype: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  useEffect(() => {
    fetchDrillHoleTypes();
  }, []);

  const fetchDrillHoleTypes = async () => {
    try {
      const response = await GoldFinderApi.get("/admin/drill-hole-types");
      setDrillHoleTypes(response.data.drillHoleTypes);
    } catch (error) {
      console.error('Error fetching drill hole types:', error);
    }
  };

  const handleEdit = (row) => {
    setEditingRow(row.anumber);
    setEditValues({
      multiplier: row.multiplier,
      description: row.description || '',
      holetype: row.holetype
    });
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditValues({});
  };

  const handleSave = async (anumber) => {
    setLoading(true);
    try {
      const response = await GoldFinderApi.put(`/admin/modifiers/drillholes/${anumber}`, {
        multiplier: editValues.multiplier,
        description: editValues.description,
        holetype: editValues.holetype
      });
      
      if (response.data.done) {
        setEditingRow(null);
        setEditValues({});
        if (refreshData) {
          refreshData();
        }
      }
    } catch (error) {
      console.error('Error updating drill hole modifier:', error);
      alert('Failed to update drill hole modifier. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (anumber) => {
    if (window.confirm(`Are you sure you want to delete the modifier for A Number ${anumber}?`)) {
      setLoading(true);
      try {
        const response = await GoldFinderApi.delete(`/admin/modifiers/drillholes/${anumber}`);
        
        if (response.data.done) {
          if (refreshData) {
            refreshData();
          }
        }
      } catch (error) {
        console.error('Error deleting drill hole modifier:', error);
        alert('Failed to delete drill hole modifier. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const filteredModifiers = drillHoleModifier.filter(row => {
    return (
      (!filters.anumber || row.anumber?.toString().toLowerCase().includes(filters.anumber.toLowerCase())) &&
      (!filters.multiplier || row.multiplier?.toString().includes(filters.multiplier)) &&
      (!filters.description || row.description?.toLowerCase().includes(filters.description.toLowerCase())) &&
      (!filters.holetype || row.holetype?.toLowerCase().includes(filters.holetype.toLowerCase()))
    );
  });

  const sortedAndFilteredModifiers = [...filteredModifiers].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    if (sortConfig.key === 'multiplier') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
    } else if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return (
    <div style={{ 
      maxHeight: 'calc(100vh - 300px)', 
      overflowY: 'auto',
      border: '1px solid #dee2e6',
      borderRadius: '0.375rem'
    }}>
      <Table striped bordered hover style={{ marginBottom: 0 }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
          <tr>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('anumber')}
            >
              A Number {getSortIcon('anumber')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('multiplier')}
            >
              Multiplier {getSortIcon('multiplier')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('description')}
            >
              Description {getSortIcon('description')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('holetype')}
            >
              Hole Type {getSortIcon('holetype')}
            </th>
            <th style={{ width: '120px' }}>Actions</th>
          </tr>
          <tr>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.anumber}
                onChange={(e) => handleFilterChange('anumber', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.multiplier}
                onChange={(e) => handleFilterChange('multiplier', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.description}
                onChange={(e) => handleFilterChange('description', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.holetype}
                onChange={(e) => handleFilterChange('holetype', e.target.value)}
              />
            </th>
            <th style={{ width: '120px' }}></th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredModifiers.map((row, index) => (
            <tr key={row.anumber}>
              <td>{row.anumber}</td>
              <td>
                {editingRow === row.anumber ? (
                  <Form.Control
                    type="number"
                    step="0.000001"
                    value={editValues.multiplier}
                    onChange={(e) => handleInputChange('multiplier', e.target.value)}
                    size="sm"
                  />
                ) : (
                  row.multiplier
                )}
              </td>
              <td>
                {editingRow === row.anumber ? (
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editValues.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    size="sm"
                  />
                ) : (
                  row.description
                )}
              </td>
              <td>
                {editingRow === row.anumber ? (
                  <Form.Select
                    value={editValues.holetype}
                    onChange={(e) => handleInputChange('holetype', e.target.value)}
                    size="sm"
                  >
                    {drillHoleTypes.map(type => (
                      <option key={type.holetype} value={type.holetype}>
                        {type.nicename}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  row.holetype
                )}
              </td>
              <td style={{ width: '120px', whiteSpace: 'nowrap' }}>
                {editingRow === row.anumber ? (
                  <>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleSave(row.anumber)}
                      disabled={loading}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faSave} />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleEdit(row)}
                      disabled={loading}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(row.anumber)}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}